import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString, getLocale } from '../../../locale'
import {
    getDrinkList,
    createDrink,
    updateDrink,
    deleteDrink,
    DrinkInfo,
    DrinkListResponse,
    getSyrupList,
    SyrupInfo,
} from '../../../api/0x0005/drinks'
import { getChannelList, ChannelInfo } from '../../../api/0x0005/channels'
import { CatalogLocalImage, getCatalogLocalList } from '../../../api/0x0005/catalog'

const LTSP101_NO_FLAVOUR_CHANNEL = 255
const LTSP101_BASE_BRIX_RATIO_A = 1
const LTSP101_BASE_BRIX_RATIO_B = 6

type Props = {
    clientId: string
}

type State = {
    drinks: DrinkInfo[]
    channels: ChannelInfo[]
    catalogImages: CatalogLocalImage[]
    syrups: Map<number, SyrupInfo>
    loading: boolean
    showModal: boolean
    wizardStep: 1 | 2 | 3 | 4 | 5
    createMode: 'classic' | 'wizard'
    modalMode: 'create' | 'edit'
    selectedDrink?: DrinkInfo
    form: {
        drinkId: number
        title: string
        description: string
        titleHe: string
        descriptionHe: string
        imageName: string
        selectedSyrupId: number
        drinkType: 'none' | 'soda' | 'juice' | 'base'
        baseChannel: number
        flavourChannel: number
        brixRatioA: number
        brixRatioB: number
        enabled: number
    }
}

export default class DrinksTab extends Component<Props, State> {
    state: State = {
        drinks: [],
        channels: [],
        catalogImages: [],
        syrups: new Map(),
        loading: true,
        showModal: false,
        wizardStep: 1,
        createMode: 'classic',
        modalMode: 'create',
        selectedDrink: undefined,
        form: {
            drinkId: 0,
            title: '',
            description: '',
            titleHe: '',
            descriptionHe: '',
            imageName: '',
            selectedSyrupId: 0,
            drinkType: 'none',
            baseChannel: 0,
            flavourChannel: LTSP101_NO_FLAVOUR_CHANNEL,
            brixRatioA: LTSP101_BASE_BRIX_RATIO_A,
            brixRatioB: LTSP101_BASE_BRIX_RATIO_B,
            enabled: 1,
        },
    }

    async componentDidMount() {
        await Promise.all([this.loadDrinkList(), this.loadChannels(), this.loadSyrups(), this.loadCatalogImages()])
    }

    loadChannels = async () => {
        try {
            const response = await getChannelList(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({ channels: response.data.channels })
            }
        } catch (error: any) {
            console.error('Failed to load channels:', error)
        }
    }

    loadSyrups = async () => {
        try {
            const response = await getSyrupList(this.props.clientId)
            if (response.data.success === 1) {
                const syrupMap = new Map<number, SyrupInfo>()
                response.data.syrups.forEach(syrup => syrupMap.set(syrup.id, syrup))
                this.setState({ syrups: syrupMap })
            }
        } catch (error: any) {
            console.error('Failed to load syrups:', error)
        }
    }

    loadDrinkList = async () => {
        try {
            this.setState({ loading: true })
            const response = await getDrinkList(this.props.clientId)
            const data = response.data as DrinkListResponse
            if (data.success === 1) {
                this.setState({ drinks: data.drinks })
            } else {
                toast.error(getString('drinks.load_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('drinks.load_error'))
        } finally {
            this.setState({ loading: false })
        }
    }

    openCreateModal = () => {
        this.setState({
            showModal: true,
            wizardStep: 1,
            createMode: 'classic',
            modalMode: 'create',
            selectedDrink: undefined,
            form: {
                drinkId: 0,
                title: '',
                description: '',
                titleHe: '',
                descriptionHe: '',
                imageName: '',
                selectedSyrupId: 0,
                drinkType: 'none',
                baseChannel: 255,
                flavourChannel: LTSP101_NO_FLAVOUR_CHANNEL,
                brixRatioA: LTSP101_BASE_BRIX_RATIO_A,
                brixRatioB: LTSP101_BASE_BRIX_RATIO_B,
                enabled: 1,
            },
        })
    }

    openCreateWizardModal = () => {
        const autoBaseChannel = this.getAutoBaseChannelByType('juice')
        this.setState({
            showModal: true,
            wizardStep: 1,
            createMode: 'wizard',
            modalMode: 'create',
            selectedDrink: undefined,
            form: {
                drinkId: 0,
                title: '',
                description: '',
                titleHe: '',
                descriptionHe: '',
                imageName: '',
                selectedSyrupId: 0,
                drinkType: 'juice',
                baseChannel: autoBaseChannel,
                flavourChannel: LTSP101_NO_FLAVOUR_CHANNEL,
                brixRatioA: LTSP101_BASE_BRIX_RATIO_A,
                brixRatioB: LTSP101_BASE_BRIX_RATIO_B,
                enabled: 1,
            },
        })
    }

    loadCatalogImages = async () => {
        try {
            const response = await getCatalogLocalList(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({ catalogImages: response.data.images || [] })
            }
        } catch (error: any) {
            console.error('Failed to load catalog images:', error)
        }
    }

    inferDrinkType = (baseChannelIndex: number, flavourChannelIndex: number): 'none' | 'soda' | 'juice' | 'base' => {
        if (flavourChannelIndex === LTSP101_NO_FLAVOUR_CHANNEL) {
            return 'base'
        }
        const baseChannel = this.state.channels.find((ch) => ch.channelNum - 1 === baseChannelIndex)
        const channelName = baseChannel?.channelName?.trim() || ''

        if (channelName === 'סודה') {
            return 'soda'
        }

        if (
            baseChannel?.channelNum === 2 ||
            channelName === 'מים קרים' ||
            channelName.toLowerCase() === 'cold water'
        ) {
            return 'juice'
        }

        return 'none'
    }

    findSyrupFromFlavourChannel = (flavourChannelIndex: number) => {
        const flavourChannel = this.state.channels.find((ch) => ch.channelNum - 1 === flavourChannelIndex)
        if (!flavourChannel || flavourChannel.syrupId === 0) {
            return 0
        }
        return flavourChannel.syrupId
    }

    openEditModal = (drink: DrinkInfo) => {
        const selectedSyrupId = this.findSyrupFromFlavourChannel(drink.flavourChannel)
        const drinkType = this.inferDrinkType(drink.baseChannel, drink.flavourChannel)
        this.setState({
            showModal: true,
            wizardStep: 1,
            modalMode: 'edit',
            selectedDrink: drink,
            form: {
                drinkId: drink.id,
                title: drink.title,
                description: drink.description,
                titleHe: drink.titleHe,
                descriptionHe: drink.descriptionHe,
                imageName: drink.imageName || '',
                selectedSyrupId,
                drinkType,
                baseChannel: drink.baseChannel,
                flavourChannel: drink.flavourChannel,
                brixRatioA: drink.brixRatioA,
                brixRatioB: drink.brixRatioB,
                enabled: drink.enabled === 0 ? 0 : 1,
            },
        })
    }

    closeModal = () => {
        this.setState({ showModal: false, wizardStep: 1, createMode: 'classic' })
    }

    handleFormChange = (field: string, value: any) => {
        this.setState((prev) => ({
            form: { ...prev.form, [field]: value },
        }))
    }

    getAutoBaseChannelByType = (drinkType: 'none' | 'soda' | 'juice' | 'base') => {
        if (drinkType === 'none') {
            return 255
        }
        if (drinkType === 'base') {
            return 255
        }

        const solenoidChannels = this.state.channels.filter((channel) => channel.mode === 0)
        if (drinkType === 'soda') {
            const sodaChannel = solenoidChannels.find((channel) => (channel.channelName?.trim() || '') === 'סודה')
            return sodaChannel ? sodaChannel.channelNum - 1 : 255
        }

        const channelTwo = solenoidChannels.find((channel) => channel.channelNum === 2)
        if (channelTwo) {
            return channelTwo.channelNum - 1
        }

        const byName = solenoidChannels.find((channel) => {
            const name = (channel.channelName?.trim() || '').toLowerCase()
            return name === 'מים קרים' || name === 'cold water'
        })
        return byName ? byName.channelNum - 1 : 255
    }

    getLinkedFlavourChannelBySyrup = (syrupId: number) => {
        if (syrupId <= 0) {
            return 255
        }
        const linkedRelayChannel = this.state.channels.find(
            (channel) => channel.syrupId === syrupId && channel.mode === 1
        )
        return linkedRelayChannel ? linkedRelayChannel.channelNum - 1 : 255
    }

    handleDrinkTypeChange = (drinkType: 'none' | 'soda' | 'juice' | 'base') => {
        this.setState((prev) => {
            if (drinkType === 'base') {
                return {
                    form: {
                        ...prev.form,
                        drinkType,
                        flavourChannel: LTSP101_NO_FLAVOUR_CHANNEL,
                        brixRatioA: LTSP101_BASE_BRIX_RATIO_A,
                        brixRatioB: LTSP101_BASE_BRIX_RATIO_B,
                    },
                }
            }
            const autoBaseChannel = this.getAutoBaseChannelByType(drinkType)
            const nextFlavourChannel =
                autoBaseChannel === 255 ? LTSP101_NO_FLAVOUR_CHANNEL : this.getLinkedFlavourChannelBySyrup(prev.form.selectedSyrupId)
            return {
                form: {
                    ...prev.form,
                    drinkType,
                    baseChannel: autoBaseChannel,
                    flavourChannel: nextFlavourChannel,
                },
            }
        })
    }

    handleSyrupChange = (selectedSyrupId: number) => {
        this.setState((prev) => {
            if (prev.form.drinkType === 'base') {
                return {
                    form: {
                        ...prev.form,
                        selectedSyrupId,
                    },
                }
            }
            const nextFlavourChannel =
                prev.form.baseChannel === 255 ? LTSP101_NO_FLAVOUR_CHANNEL : this.getLinkedFlavourChannelBySyrup(selectedSyrupId)
            return {
                form: {
                    ...prev.form,
                    selectedSyrupId,
                    flavourChannel: nextFlavourChannel,
                },
            }
        })
    }

    getWizardSteps = (drinkType: 'none' | 'soda' | 'juice' | 'base'): Array<1 | 2 | 3 | 4 | 5> => {
        if (drinkType === 'base') {
            return [1, 3, 5]
        }
        return [1, 2, 3, 4, 5]
    }

    getDefaultEnglishFields = () => {
        const { form, syrups } = this.state
        if (form.drinkType === 'base' || form.selectedSyrupId <= 0) {
            return { title: '', description: '' }
        }
        const syrup = syrups.get(form.selectedSyrupId)
        if (!syrup) {
            return { title: '', description: '' }
        }
        const suffix = form.drinkType === 'soda' ? 'Soda' : 'Juice'
        return {
            title: `${syrup.name} ${suffix}`.trim(),
            description: syrup.description || '',
        }
    }

    applyWizardFinalDefaults = () => {
        this.setState(prev => {
            const { title, description } = this.getDefaultEnglishFields()
            return {
                form: {
                    ...prev.form,
                    title: prev.form.title || title,
                    description: prev.form.description || description,
                },
            }
        })
    }

    goWizardNext = () => {
        this.setState(prev => {
            const steps = this.getWizardSteps(prev.form.drinkType)
            const index = steps.indexOf(prev.wizardStep)
            const nextStep = index >= 0 && index < steps.length - 1 ? steps[index + 1] : prev.wizardStep
            return { wizardStep: nextStep }
        }, () => {
            if (this.state.wizardStep === 5) {
                this.applyWizardFinalDefaults()
            }
        })
    }

    goWizardBack = () => {
        this.setState(prev => {
            const steps = this.getWizardSteps(prev.form.drinkType)
            const index = steps.indexOf(prev.wizardStep)
            const prevStep = index > 0 ? steps[index - 1] : prev.wizardStep
            return { wizardStep: prevStep }
        })
    }

    renderWizardStep = () => {
        const { wizardStep, form, syrups, catalogImages } = this.state
        const isHebrew = getLocale() === 'he'

        if (wizardStep === 1) {
            return (
                <div className="space-y-3">
                    <div className="text-sm text-gray-600 dark:text-gray-300">{isHebrew ? 'שלב 1: סוג משקה' : 'Step 1: Drink type'}</div>
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { key: 'juice', labelHe: 'מיץ', labelEn: 'Juice' },
                            { key: 'soda', labelHe: 'סודה', labelEn: 'Soda' },
                            { key: 'base', labelHe: 'בסיס', labelEn: 'Base' },
                        ].map(item => (
                            <button
                                key={item.key}
                                type="button"
                                onClick={() => this.handleDrinkTypeChange(item.key as 'juice' | 'soda' | 'base')}
                                className={`rounded border px-4 py-4 text-sm font-semibold ${
                                    form.drinkType === item.key
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600'
                                }`}
                            >
                                {isHebrew ? item.labelHe : item.labelEn}
                            </button>
                        ))}
                    </div>
                </div>
            )
        }

        if (wizardStep === 2) {
            return (
                <div className="space-y-3">
                    <div className="text-sm text-gray-600 dark:text-gray-300">{isHebrew ? 'שלב 2: בחר סירופ' : 'Step 2: Choose syrup'}</div>
                    <select
                        value={form.selectedSyrupId}
                        onChange={(e) => this.handleSyrupChange(parseInt(e.target.value, 10) || 0)}
                        className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                    >
                        <option value={0}>{isHebrew ? 'ללא סירופ' : 'None'}</option>
                        {Array.from(syrups.values()).map((syrup) => {
                            const syrupName = isHebrew && syrup.nameHe ? syrup.nameHe : syrup.name
                            return (
                                <option key={syrup.id} value={syrup.id}>
                                    {syrupName}
                                </option>
                            )
                        })}
                    </select>
                </div>
            )
        }

        if (wizardStep === 3) {
            return (
                <div className="space-y-3">
                    <div className="text-sm text-gray-600 dark:text-gray-300">{isHebrew ? 'שלב 3: בחר תמונה' : 'Step 3: Choose image'}</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <button
                            type="button"
                            onClick={() => this.handleFormChange('imageName', '')}
                            className={`border rounded-lg p-3 text-center ${
                                form.imageName === '' ? 'border-blue-600 ring-1 ring-blue-600' : 'border-gray-300 dark:border-gray-600'
                            }`}
                        >
                            <div className="h-20 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-300">
                                {isHebrew ? 'ללא תמונה' : 'No image'}
                            </div>
                            <div className="mt-2 text-xs dark:text-white">{isHebrew ? 'ללא תמונה' : 'No image'}</div>
                        </button>
                        {catalogImages.map((image) => (
                            <button
                                key={image.id}
                                type="button"
                                onClick={() => this.handleFormChange('imageName', image.name)}
                                className={`border rounded-lg p-3 text-center ${
                                    form.imageName === image.name ? 'border-blue-600 ring-1 ring-blue-600' : 'border-gray-300 dark:border-gray-600'
                                }`}
                            >
                                <div className="h-20 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-300">
                                    {image.width}x{image.height}
                                </div>
                                <div className="mt-2 text-xs dark:text-white truncate" title={image.title || image.name}>
                                    {image.title || image.name}
                                </div>
                                <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate" title={image.name}>
                                    {image.name}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )
        }

        if (wizardStep === 4) {
            return (
                <div className="space-y-3">
                    <div className="text-sm text-gray-600 dark:text-gray-300">{isHebrew ? 'שלב 4: יחס בריקס' : 'Step 4: Brix ratio'}</div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.brix_ratio_a')}</label>
                            <input
                                type="number"
                                value={form.brixRatioA}
                                className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                                readOnly
                                disabled
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.brix_ratio_b')}</label>
                            <input
                                type="number"
                                value={form.brixRatioB}
                                onChange={(e) => this.handleFormChange('brixRatioB', parseInt(e.target.value, 10) || 0)}
                                className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                min={1}
                                max={255}
                            />
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <div className="space-y-4">
                <div className="text-sm text-gray-600 dark:text-gray-300">{isHebrew ? 'שלב 5: פרטי המשקה' : 'Step 5: Drink details'}</div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.title_en')} *</label>
                        <input
                            type="text"
                            value={form.title}
                            onChange={(e) => this.handleFormChange('title', e.target.value)}
                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                            maxLength={100}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.title_he')} *</label>
                        <input
                            type="text"
                            value={form.titleHe}
                            onChange={(e) => this.handleFormChange('titleHe', e.target.value)}
                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                            dir="rtl"
                            maxLength={100}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.description_en')}</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => this.handleFormChange('description', e.target.value)}
                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                            rows={3}
                            maxLength={500}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.description_he')}</label>
                        <textarea
                            value={form.descriptionHe}
                            onChange={(e) => this.handleFormChange('descriptionHe', e.target.value)}
                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                            rows={3}
                            dir="rtl"
                            maxLength={500}
                        />
                    </div>
                </div>
                <label className="flex items-center space-x-2 dark:text-white">
                    <input
                        type="checkbox"
                        checked={form.enabled === 1}
                        onChange={(e) => this.handleFormChange('enabled', e.target.checked ? 1 : 0)}
                    />
                    <span>{isHebrew ? 'משקה פעיל' : 'Drink enabled'}</span>
                </label>
            </div>
        )
    }

    handleSave = async () => {
        const { form, modalMode } = this.state
        const isBaseDrink = form.drinkType === 'base'
        const flavourChannel = isBaseDrink ? LTSP101_NO_FLAVOUR_CHANNEL : form.flavourChannel
        const brixRatioA = isBaseDrink ? LTSP101_BASE_BRIX_RATIO_A : form.brixRatioA
        const brixRatioB = isBaseDrink ? LTSP101_BASE_BRIX_RATIO_B : form.brixRatioB

        if (!form.title.trim() || !form.titleHe.trim()) {
            toast.error(getString('drinks.required_fields'))
            return
        }

        try {
            if (modalMode === 'create') {
                const response = await createDrink(this.props.clientId, {
                    title: form.title,
                    description: form.description,
                    titleHe: form.titleHe,
                    descriptionHe: form.descriptionHe,
                    baseChannel: form.baseChannel,
                    flavourChannel,
                    brixRatioA,
                    brixRatioB,
                    enabled: form.enabled,
                    imageName: form.imageName,
                })

                if (response.data.success === 1) {
                    toast.success(getString('drinks.create_success'))
                    this.closeModal()
                    await this.loadDrinkList()
                } else {
                    toast.error(response.data.errorMsg || getString('drinks.create_error'))
                }
            } else {
                const response = await updateDrink(this.props.clientId, {
                    drinkId: form.drinkId,
                    title: form.title,
                    description: form.description,
                    titleHe: form.titleHe,
                    descriptionHe: form.descriptionHe,
                    baseChannel: form.baseChannel,
                    flavourChannel,
                    brixRatioA,
                    brixRatioB,
                    enabled: form.enabled,
                    imageName: form.imageName,
                })

                if (response.data.success === 1) {
                    toast.success(getString('drinks.update_success'))
                    this.closeModal()
                    await this.loadDrinkList()
                } else {
                    toast.error(response.data.errorMsg || getString('drinks.update_error'))
                }
            }
        } catch (error: any) {
            toast.error(error?.message || getString('drinks.save_error'))
        }
    }

    handleDelete = async (drinkId: number) => {
        if (!window.confirm(getString('drinks.delete_confirm'))) return

        try {
            const response = await deleteDrink(this.props.clientId, drinkId)
            if (response.data.success === 1) {
                toast.success(getString('drinks.delete_success'))
                await this.loadDrinkList()
            } else {
                toast.error(response.data.errorMsg || getString('drinks.delete_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('drinks.delete_error'))
        }
    }

    render() {
        const { drinks, channels, catalogImages, syrups, loading, showModal, wizardStep, createMode, modalMode, form } = this.state

        return (
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold dark:text-white">{getString('drinks.title')}</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={this.openCreateModal}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            {getString('drinks.add')}
                        </button>
                        <button
                            onClick={this.openCreateWizardModal}
                            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                        >
                            {getLocale() === 'he' ? 'אשף משקה' : 'Drink Wizard'}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-8 dark:text-white">{getString('common.loading')}</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {drinks.map((drink) => {
                            const isHebrew = getLocale() === 'he'
                            const displayTitle = (isHebrew && drink.titleHe) ? drink.titleHe : drink.title
                            const displayDescription = (isHebrew && drink.descriptionHe) ? drink.descriptionHe : drink.description
                            
                            // Find channels and their syrups
                            const baseChannel = channels.find(ch => ch.channelNum - 1 === drink.baseChannel)
                            const baseSyrup = baseChannel ? syrups.get(baseChannel.syrupId) : undefined
                            const baseSyrupName = baseSyrup ? (isHebrew && baseSyrup.nameHe ? baseSyrup.nameHe : baseSyrup.name) : ''
                            
                            const flavourChannel = drink.flavourChannel !== 255 ? channels.find(ch => ch.channelNum - 1 === drink.flavourChannel) : undefined
                            const flavourSyrup = flavourChannel ? syrups.get(flavourChannel.syrupId) : undefined
                            const flavourSyrupName = flavourSyrup ? (isHebrew && flavourSyrup.nameHe ? flavourSyrup.nameHe : flavourSyrup.name) : ''
                            
                            // Find base drink (drink with same base channel but no flavour)
                            const baseDrink = drink.flavourChannel !== 255 ? drinks.find(d => d.baseChannel === drink.baseChannel && d.flavourChannel === 255) : undefined
                            const baseDrinkName = baseDrink ? (isHebrew && baseDrink.titleHe ? baseDrink.titleHe : baseDrink.title) : ''
                            
                            return (
                            <div key={drink.id} className="border dark:border-gray-700 rounded-lg p-4 shadow hover:shadow-md dark:bg-gray-800">
                                <h3 className="text-lg font-semibold mb-2 dark:text-white">{displayTitle}</h3>
                                <div className={`text-xs font-semibold mb-2 ${drink.enabled === 1 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {getLocale() === 'he'
                                        ? (drink.enabled === 1 ? 'פעיל' : 'מושבת')
                                        : (drink.enabled === 1 ? 'Enabled' : 'Disabled')}
                                </div>
                                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">{displayDescription}</p>
                                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 mb-3">
                                    <div>
                                        {getString('drinks.base_channel')}: {getString('channels.channel')} {baseChannel ? baseChannel.channelNum : drink.baseChannel + 1}
                                        {baseSyrupName && <span className="text-blue-600 dark:text-blue-400 ml-1">({baseSyrupName})</span>}
                                    </div>
                                    <div>
                                        {getString('drinks.flavour_channel')}: {drink.flavourChannel === 255 ? getString('drinks.no_flavour') : (flavourSyrupName || `${getString('channels.channel')} ${drink.flavourChannel + 1}`)}
                                    </div>
                                    {baseDrinkName && (
                                        <div>
                                            {getString('drinks.base_drink')}: <span className="text-green-600 dark:text-green-400">{baseDrinkName}</span>
                                        </div>
                                    )}
                                    <div>
                                        {getString('drinks.brix_ratio')}: {drink.brixRatioA}/{drink.brixRatioB}
                                    </div>
                                    <div>
                                        {getLocale() === 'he' ? 'תמונת משקה' : 'Drink image'}: {drink.imageName || (getLocale() === 'he' ? 'ללא' : 'None')}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => this.openEditModal(drink)}
                                        className="flex-1 px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600"
                                    >
                                        {getString('drinks.edit')}
                                    </button>
                                    <button
                                        onClick={() => this.handleDelete(drink.id)}
                                        className="flex-1 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                                    >
                                        {getString('common.delete')}
                                    </button>
                                </div>
                            </div>
                            )
                        })}
                        {drinks.length === 0 && (
                            <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                                {getString('drinks.empty')}
                            </div>
                        )}
                    </div>
                )}

                {/* Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <h3 className="text-xl font-bold mb-4 dark:text-white">
                                {modalMode === 'create' ? getString('drinks.add') : getString('drinks.edit')}
                            </h3>

                            {modalMode === 'create' && createMode === 'wizard' ? (
                                <>
                                    {this.renderWizardStep()}
                                    <div className="flex justify-between gap-2 mt-6">
                                        <button
                                            onClick={this.closeModal}
                                            className="px-4 py-2 border dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                                        >
                                            {getString('common.cancel')}
                                        </button>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={this.goWizardBack}
                                                disabled={wizardStep === this.getWizardSteps(form.drinkType)[0]}
                                                className="px-4 py-2 border dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                                            >
                                                {getLocale() === 'he' ? 'הקודם' : 'Back'}
                                            </button>
                                            {wizardStep === this.getWizardSteps(form.drinkType)[this.getWizardSteps(form.drinkType).length - 1] ? (
                                                <button
                                                    onClick={this.handleSave}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                                >
                                                    {getString('common.save')}
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={this.goWizardNext}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                                >
                                                    {getLocale() === 'he' ? 'הבא' : 'Next'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.title_en')} *</label>
                                                <input
                                                    type="text"
                                                    value={form.title}
                                                    onChange={(e) => this.handleFormChange('title', e.target.value)}
                                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                    maxLength={100}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.title_he')} *</label>
                                                <input
                                                    type="text"
                                                    value={form.titleHe}
                                                    onChange={(e) => this.handleFormChange('titleHe', e.target.value)}
                                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                    dir="rtl"
                                                    maxLength={100}
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1 dark:text-white">{getLocale() === 'he' ? 'סוג משקה' : 'Drink type'}</label>
                                                <select
                                                    value={form.drinkType}
                                                    onChange={(e) => this.handleDrinkTypeChange(e.target.value as 'none' | 'soda' | 'juice' | 'base')}
                                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                >
                                                    <option value="none">{getLocale() === 'he' ? 'מותאם אישית' : 'Custom'}</option>
                                                    <option value="juice">{getLocale() === 'he' ? 'מיץ' : 'Juice'}</option>
                                                    <option value="soda">{getLocale() === 'he' ? 'סודה' : 'Soda'}</option>
                                                    <option value="base">{getLocale() === 'he' ? 'בסיס' : 'Base'}</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1 dark:text-white">{getLocale() === 'he' ? 'סירופ' : 'Syrup'}</label>
                                                <select
                                                    value={form.selectedSyrupId}
                                                    onChange={(e) => this.handleSyrupChange(parseInt(e.target.value, 10) || 0)}
                                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                >
                                                    <option value={0}>{getLocale() === 'he' ? 'ללא סירופ' : 'None'}</option>
                                                    {Array.from(syrups.values()).map((syrup) => (
                                                        <option key={syrup.id} value={syrup.id}>
                                                            {getLocale() === 'he' && syrup.nameHe ? syrup.nameHe : syrup.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-1 dark:text-white">{getLocale() === 'he' ? 'תמונת משקה' : 'Drink image'}</label>
                                            <select
                                                value={form.imageName}
                                                onChange={(e) => this.handleFormChange('imageName', e.target.value)}
                                                className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                            >
                                                <option value="">{getLocale() === 'he' ? 'ללא' : 'None'}</option>
                                                {catalogImages.map((image) => (
                                                    <option key={image.id} value={image.name}>
                                                        {image.title || image.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.base_channel')}</label>
                                                <select
                                                    value={form.baseChannel}
                                                    onChange={(e) => this.handleFormChange('baseChannel', parseInt(e.target.value, 10) || 255)}
                                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                >
                                                    <option value={255}>{getString('drinks.no_flavour')}</option>
                                                    {channels.filter(ch => ch.mode === 0).map(ch => (
                                                        <option key={ch.channelNum} value={ch.channelNum - 1}>
                                                            {getString('channels.channel')} {ch.channelNum}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {form.drinkType !== 'base' && (
                                                <div>
                                                    <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.flavour_channel')}</label>
                                                    <select
                                                        value={form.flavourChannel}
                                                        onChange={(e) => this.handleFormChange('flavourChannel', parseInt(e.target.value, 10) || 255)}
                                                        className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                    >
                                                        <option value={255}>{getString('drinks.no_flavour')}</option>
                                                        {channels.filter(ch => ch.mode === 1).map(ch => (
                                                            <option key={ch.channelNum} value={ch.channelNum - 1}>
                                                                {getString('channels.channel')} {ch.channelNum}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>

                                        {form.drinkType !== 'base' && (
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.brix_ratio_a')}</label>
                                                    <input
                                                        type="number"
                                                        value={form.brixRatioA}
                                                        readOnly
                                                        disabled
                                                        className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.brix_ratio_b')}</label>
                                                    <input
                                                        type="number"
                                                        value={form.brixRatioB}
                                                        onChange={(e) => this.handleFormChange('brixRatioB', parseInt(e.target.value, 10) || 0)}
                                                        className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                        min={1}
                                                        max={255}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.description_en')}</label>
                                                <textarea
                                                    value={form.description}
                                                    onChange={(e) => this.handleFormChange('description', e.target.value)}
                                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                    rows={3}
                                                    maxLength={500}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1 dark:text-white">{getString('drinks.description_he')}</label>
                                                <textarea
                                                    value={form.descriptionHe}
                                                    onChange={(e) => this.handleFormChange('descriptionHe', e.target.value)}
                                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                    rows={3}
                                                    dir="rtl"
                                                    maxLength={500}
                                                />
                                            </div>
                                        </div>

                                        <label className="flex items-center space-x-2 dark:text-white">
                                            <input
                                                type="checkbox"
                                                checked={form.enabled === 1}
                                                onChange={(e) => this.handleFormChange('enabled', e.target.checked ? 1 : 0)}
                                            />
                                            <span>{getLocale() === 'he' ? 'משקה פעיל' : 'Drink enabled'}</span>
                                        </label>
                                    </div>

                                    <div className="flex justify-end gap-2 mt-6">
                                        <button
                                            onClick={this.closeModal}
                                            className="px-4 py-2 border dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                                        >
                                            {getString('common.cancel')}
                                        </button>
                                        <button
                                            onClick={this.handleSave}
                                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >
                                            {getString('common.save')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        )
    }
}
